import {beginMark, endMark, supportsUserTiming} from './UserTimingUtil';
type Action = {
  type: string;
};

// uglify isn't eliminating using this variable due to babel converting the const to a var
// the timer calls are currently wrapped in env checks external but
// still useful to have here as additional check
const enableUserTimingAPI = process.env.NODE_ENV !== 'production';

const getActionLabel = (
  store: string,
  phase: StorePhase | null,
  isDispatching: boolean
) => {
  if (isDispatching === false || phase === null) {
    return `${store} [listener]`;
  } else {
    return `${store}.${phase}`;
  }
};

// this returns a boolean so in the future we could opt out of starting a timing
const beginActionMark = (
  action: Action,
  store: string,
  phase: StorePhase
): boolean => {
  const label = getActionLabel(store, phase, isDispatching);
  beginMark(label);
  return true;
};

const endActionMark = (
  action: Action,
  store: string,
  phase: StorePhase,
  warning: string | null
) => {
  const label = getActionLabel(store, phase, isDispatching);
  endMark(label, label, warning);
};

type StorePhase = 'update' | 'notify';

let isDispatching = false;
let currentAction: Action | null = null;
let currentContainer: string | null = null;
let currentStorePhase: StorePhase | null = null;
let effectCountInCurrentDispatch = 0;

export function recordEffect(): void {
  if (enableUserTimingAPI) {
    effectCountInCurrentDispatch++;
  }
}

export function startContainerUpdateTimer(containerName: string): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }
    currentContainer = containerName;
    beginMark('(Updating Container)');
  }
}

export function stopContainerUpdateTimer(): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }
    const container = currentContainer || 'FluxContainer';
    currentContainer = null;
    endMark(
      `(Updating Container: ${String(container)})`,
      '(Updating Container)',
      null
    );
  }
}

export function startDispatchTimer(action: Action): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }
    isDispatching = true;
    currentAction = action;
    beginMark('(Dispatching Action)');
  }
}

export function stopDispatchTimer(): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }

    // we should always have a currentAction when calling this but just in case.
    const actionType = currentAction ? currentAction.type : 'Unknown Action';

    isDispatching = false;
    currentAction = null;
    endMark(
      `(Dispatching Action: ${actionType})`,
      '(Dispatching Action)',
      null
    );
  }
}

export function startStoreTimer(
  action: Action,
  store: string,
  phase: StorePhase
): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }

    if (!currentAction && action) {
      currentAction = action;
    }

    if (!currentAction) {
      return;
    }

    if (!beginActionMark(currentAction, store, phase)) {
      return;
    }

    // update the currentStorePhase
    currentStorePhase = phase;
  }
}

export function stopStoreTimer(store: string): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }

    if (currentStorePhase !== null && currentAction !== null) {
      endActionMark(currentAction, store, currentStorePhase, null);
      storeUpdateCountInCurrentDispatch++;
    }

    currentStorePhase = null;
  }
}

export function startNotifySubscribersTimer(): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }

    effectCountInCurrentDispatch = 0;
    beginMark('(Notifying Subscribers)');
  }
}

export function stopNotifySubscribersTimer(): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }

    const count = effectCountInCurrentDispatch;
    effectCountInCurrentDispatch = 0;

    endMark(
      `(Notifying Subscribers: ${count} Total)`,
      '(Notifying Subscribers)',
      null
    );
  }
}

let storeUpdateCountInCurrentDispatch = 0;
export function startStoreUpdatesTimer(action: Action): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }
    currentAction = action;
    storeUpdateCountInCurrentDispatch = 0;
    beginMark('(Flux Stores Updating)');
  }
}

export function stopStoreUpdatesTimer(): void {
  if (enableUserTimingAPI) {
    if (!supportsUserTiming) {
      return;
    }

    const count = storeUpdateCountInCurrentDispatch;
    storeUpdateCountInCurrentDispatch = 0;
    endMark(
      `(Flux Stores Updating: ${count} Total)`,
      '(Flux Stores Updating)',
      null
    );
  }
}

// [FUTURE]: use request callback timers for listener timings
let isWaitingForCallback = false;
export function startRequestCallbackTimer(): void {
  if (enableUserTimingAPI) {
    if (supportsUserTiming && !isWaitingForCallback) {
      isWaitingForCallback = true;
      beginMark('(Waiting for async callback...)');
    }
  }
}

export function stopRequestCallbackTimer(didExpire: boolean): void {
  if (enableUserTimingAPI) {
    if (supportsUserTiming) {
      isWaitingForCallback = false;
      const warning = didExpire ? 'Flux was blocked by main thread' : null;
      endMark(
        '(Waiting for async callback...)',
        '(Waiting for async callback...)',
        warning
      );
    }
  }
}
